<template>
  <router-view></router-view>
</template>

<script setup>
import { onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";

const store = useStore();
const availabilityPollIntervalMs = Number(
  process.env.VUE_APP_AVAILABILITY_POLL_INTERVAL_MS || 300000
);

let availabilityPollTimer = null;

function fetchAvailability() {
  store.dispatch("fetchAvailability");
}

function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    fetchAvailability();
  }
}

onMounted(() => {
  fetchAvailability();

  availabilityPollTimer = window.setInterval(() => {
    fetchAvailability();
  }, availabilityPollIntervalMs);

  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onUnmounted(() => {
  if (availabilityPollTimer) {
    window.clearInterval(availabilityPollTimer);
    availabilityPollTimer = null;
  }

  document.removeEventListener("visibilitychange", handleVisibilityChange);
});
</script>

<style>
@import '@/assets/styles/variables.css';

body {
    margin: 0;
    padding: 0;
    font-family: var(--font-family-primary);
    font-size: var(--font-size-body);
    line-height: var(--line-height-body);
    font-weight: var(--font-weight-regular);
    color: var(--color-white);
    background-color: var(--color-background);
}

.body-text {
    font-family: var(--font-family-primary);
    font-size: var(--font-size-body);
    line-height: var(--line-height-body);
    font-weight: var(--font-weight-regular);
    color: var(--color-white);
}

.header-1 {
    font-family: var(--font-family-primary);
    font-size: var(--font-size-h1);
    line-height: var(--line-height-h1);
    font-weight: var(--font-weight-semibold);
    color: var(--color-white);
}

.header-2 {
    font-family: var(--font-family-primary);
    font-size: var(--font-size-h2);
    line-height: var(--line-height-h2);
    font-weight: var(--font-weight-medium);
    color: var(--color-white);
}

.caption {
    font-family: var(--font-family-primary);
    font-size: var(--font-size-caption);
    line-height: var(--line-height-caption);
    font-weight: var(--font-weight-regular);
    color: var(--color-white);
}

.description {
    font-family: var(--font-family-primary);
    font-size: var(--font-size-description);
    line-height: var(--line-height-description);
    font-weight: var(--font-weight-regular);
    color: var(--color-white);
}

.tag {
    font-family: var(--font-family-primary);
    font-size: var(--font-size-tag);
    line-height: var(--line-height-tag);
    font-weight: var(--font-weight-medium);
    color: var(--color-white);
    text-transform: uppercase;
}

.control-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--control-btn-size);
    height: var(--control-btn-size);
    padding: var(--control-btn-padding);
    background-color: var(--bg-control-btn);
    border-radius: var(--border-radius-md);
    cursor: pointer;
}

button {
    background: none;       /* Removes any background properties */
    border: none;           /* Removes border */
    color: inherit;         /* Inherits color from parent */
    font: inherit;          /* Inherits font properties from parent */
    line-height: normal;    /* Resets line-height to a normal value */
    overflow: visible;      /* Ensures the content is not clipped */
    padding: 0;             /* Removes padding */
    -webkit-appearance: none; /* Removes platform-native styling on iOS */
    -moz-appearance: none;    /* Removes platform-native styling on Firefox */
    appearance: none;         /* Removes platform-native styling */
    cursor: pointer;         /* Ensures the cursor appears as a pointer */
    align-items: flex-start; /* Align items at the start in flex layouts */
    text-align: start;       /* Aligns text to the start */
    text-transform: none;    /* No text transformation */
    white-space: nowrap;     /* Prevents wrapping of text */
    background-color: transparent; /* Transparent background */
    border-radius: 0;        /* No border radius */
}

button:focus {
    outline: none;
}

button:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
}

button:active,
button:hover {
    background-color: transparent;
}

.gradient-text {
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline;
}

.gray-text {
    color: var(--color-white-70);
}

::-webkit-scrollbar-button {
  display: none;
}

::-webkit-scrollbar {
  width: var(--scrollbar-width);
  height: var(--scrollbar-height);
}

::-webkit-scrollbar-track {
  background: var(--color-white-10);
  border-radius: var(--border-radius-xs);
}

::-webkit-scrollbar-thumb {
  background: var(--color-white-20);
  border-radius: var(--border-radius-xs);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-white-30);
}

/* Firefox scrollbar styling */
* {
  scrollbar-width: thin;
  scrollbar-color: var(--color-white-20) var(--color-white-10);
}

.grecaptcha-badge { visibility: hidden; }
</style>
